import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';
import { API } from '../../App';
import { CheckCircle2, X, FileText, Loader2, AlertCircle } from 'lucide-react';
import StreamingUploadModal from '../bankfeed/StreamingUploadModal';

/**
 * GlobalUploadProgressBanner - iter1230
 * -----------------------------------------------------------------
 * Full-width, non-dismissible top banner that mounts inside
 * DashboardLayout above the routed page content. It matches the
 * visual weight of the other global banners (Mono catch-up, backlog
 * progress, pending opening balance) so users notice statement-upload
 * activity on EVERY authenticated page (Home, Bills, Invoices,
 * Insights, Banks, Bank Feeds, etc.).
 *
 * Scope: this banner is the POST-backlog PROCESSING indicator only.
 * The backlog-fee confirmation stage is handled by a global MODAL
 * (GlobalUploadBacklogModal) - see the sibling component. Jobs that
 * are still `awaiting_confirmation` / `pending_payment` are filtered
 * out here so the two surfaces do not overlap or confuse the user.
 *
 * (Component still exported as `GlobalUploadProgressBadge` for
 * historical import stability inside DashboardLayout.)
 */

// Statuses that belong to the "processing" phase this banner covers.
// Awaiting-confirmation / pending-payment are DELIBERATELY EXCLUDED -
// they are surfaced by the modal instead.
// iter1237 - `timeout` removed: it was a client-side lie invented by
// the 5-minute poll cap. The backend now owns the terminal state.
const BANNER_STATUSES = new Set([
  'queued', 'processing', 'needs_mapping', 'completed', 'failed',
  'learning', 'resuming',   // C′ · self-healing loop: the upload teaches itself a new layout, then re-runs
  // iter1241 · terminal-but-loud statuses. Without these, a job that
  // flips to `blocked_continuity` mid-view is silently filtered out of
  // the banner and the user sees the pill vanish with no explanation.
  'blocked_continuity',
]);

const isActive = (job) =>
  job.status === 'queued' || job.status === 'processing' || job.status === 'needs_mapping' ||
  job.status === 'learning' || job.status === 'resuming';

const isAwaiting = () => false;   // awaiting_confirmation lives in the modal

// iter1241 · Case-specific copy for continuity-blocked uploads. Always
// prefers the backend's `status_message` when present (it already
// carries the exact numbers), and falls back to a hand-written line
// so nothing shows as blank.
const continuityBlockPhrase = (job) => {
  if (job.statusMessage) return job.statusMessage;
  const rep = job.continuityReport || {};
  const caseKey = rep.case || job.cancelReason || 'blocked';
  if (caseKey === 'fully_covered') {
    return `This statement (${rep.new_period_start || '?'} → ${rep.new_period_end || '?'}) is already in your books. Nothing to add.`;
  }
  if (caseKey === 'gap_detected') {
    return `Gap detected: books close on ${rep.last_close_date || '?'} at ${rep.books_balance}, statement opens at ${rep.statement_balance}. Upload the missing statement first.`;
  }
  if (caseKey === 'continuity_mismatch') {
    return `Continuity mismatch on ${rep.last_close_date || '?'}: books ${rep.books_balance} vs statement ${rep.statement_balance}. Check the file matches this account.`;
  }
  if (caseKey === 'no_boundary_row') {
    return `The new statement overlaps existing books through ${rep.last_close_date || '?'} but has no row on or before that date to verify continuity.`;
  }
  if (caseKey === 'backlog_charge_declined') {
    return `Upload cancelled — payment declined. Update your card and re-upload.`;
  }
  return 'This upload was blocked. Please review the details.';
};

const statusPhrase = (job) => {
  if (job.status === 'blocked_continuity') return continuityBlockPhrase(job);
  // iter1245 · Background-first execution.
  // Prefer a stable, monotonic "Posted X of Y" message driven by the
  // backend's shard-based `feed_posted_count` + `total_canonical_txs`
  // counters. This hides the "processing was interrupted" jitter that
  // the previous 30-min-mega-task pipeline used to expose to users
  // (Mono/Plaid don't show it either).
  const posted = Number(job.feedPostedCount ?? job.feed_posted_count);
  const total = Number(job.totalCanonicalTxs ?? job.total_canonical_txs);
  if (Number.isFinite(posted) && Number.isFinite(total) && total > 0) {
    if (posted >= total) return 'Finalising — posting last checks…';
    return `Posting transactions to bank feed (${posted} of ${total})…`;
  }
  // Suppress interrupted/resuming copy from the visible banner —
  // internal retry churn should never leak into the UX. Fall back to a
  // stable "Still processing" line while the backend catches up.
  const raw = (job.statusMessage || '').toLowerCase();
  const isChurn = (
    raw.includes('interrupted')
    || raw.includes('resuming')
    || raw.includes('automatically resuming')
    || raw.includes('resume')
  );
  if (isChurn) return 'Still processing — this can take a few minutes on large files…';
  if (job.statusMessage) return job.statusMessage;
  switch (job.status) {
    case 'needs_mapping':
      return 'Needs column mapping';
    case 'processing':
      return 'Processing your statement in the background…';
    case 'learning':
      return 'New statement layout — teaching ourselves to read it (1–2 min)…';
    case 'resuming':
      return 'Re-reading your statement with what we just learned…';
    case 'queued':
      return 'Queued for processing';
    case 'completed':
      return 'Ready in Bank Feed';
    case 'failed':
      return 'Upload failed';
    default:
      return 'Uploading…';
  }
};

const GlobalUploadProgressBadge = () => {
  const { jobs, removeJob } = useBackgroundJobs();
  const navigate = useNavigate();
  const location = useLocation();
  const onBankFeedsPage = (location.pathname || '').startsWith('/bank-feeds');

  // iter1238 Phase 8.11 · Modal state for both "View progress" (active
  // jobs) and "Review failure" (failed jobs). Replaces the old
  // navigate-to-/bank-feeds behavior, which felt dead when the user was
  // already on that page and buried the failure details behind a page
  // navigation. Modal is scrollable + closable.
  // iter1238 Phase 8.13b · Cancel-in-flight tracking so the button
  // shows a spinner + "Cancelling…" label immediately, closing the
  // ~800 ms gap between click and card removal.
  const [cancellingIds, setCancellingIds] = useState(() => new Set());

  // Only surface jobs whose status belongs to the processing phase.
  // awaiting_confirmation / pending_payment are shown via the modal.
  // iter1238 Phase 8.13d · Declared BEFORE `modalJob` useMemo below —
  // the previous order caused a TDZ "Cannot access 'visibleJobs' before
  // initialization" crash on /dashboard.
  const visibleJobs = (jobs || []).filter((j) => BANNER_STATUSES.has(j.status));

  // iter1238 Phase 8.13b · Modal state stores jobId only (not the
  // whole job snapshot). We resolve the live job from `visibleJobs`
  // on every render so the modal's progress/status_message keep
  // ticking as the parent context polls.
  const [modalJobId, setModalJobId] = useState(null);
  const modalJob = React.useMemo(
    () => (modalJobId ? visibleJobs.find((j) => j.jobId === modalJobId) : null),
    [modalJobId, visibleJobs],
  );

  if (visibleJobs.length === 0) return null;

  const activeJobs = visibleJobs.filter(isActive);
  const completedJobs = visibleJobs.filter((j) => j.status === 'completed');
  const failedJobs = visibleJobs.filter(
    (j) => j.status === 'failed' || j.status === 'blocked_continuity'
  );

  // Pick the most urgent job to headline the banner (failed > active > done).
  const headline =
    failedJobs[0] || activeJobs[0] || completedJobs[0] || visibleJobs[0];

  const tone = failedJobs.length
    ? 'failed'
    : activeJobs.length
    ? 'active'
    : 'done';

  const toneClasses = {
    active:  'bg-indigo-50 border-indigo-200 text-indigo-900',
    failed:  'bg-rose-50 border-rose-200 text-rose-900',
    done:    'bg-emerald-50 border-emerald-200 text-emerald-900',
  }[tone];

  const icon = {
    active:   <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />,
    failed:   <AlertCircle className="w-4 h-4 text-rose-600" />,
    done:     <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  }[tone];

  const pct = Math.max(0, Math.min(100, headline.progress || 0));
  const otherCount = visibleJobs.length - 1;

  // iter1234-hotfix + iter1238 Phase 8.11 —
  //  - Failed jobs: open the modal with failure details + Retry button.
  //  - Active jobs: open the modal with the live parse feed (no more
  //    navigation to /bank-feeds; the modal is available from every
  //    authenticated page including the current one).
  //  - Completed jobs: navigate to /bank-feeds?tab=uploads so the user
  //    can inspect the final result.
  //
  // iter1244 · For a terminal-blocked upload (`blocked_continuity`,
  // `rejected`, `layout_reject`) the retry re-runs the same pipeline
  // over the same cached rows and hits the same deterministic block.
  // Offering "Retry" is misleading — the underlying cause hasn't
  // changed. We surface a read-only message with a Dismiss button
  // instead. The user's path forward is to re-upload a corrected file
  // (which is a NEW job, not a retry).
  const isTerminalBlocked = (
    headline.status === 'blocked_continuity' ||
    headline.status === 'rejected' ||
    headline.status === 'layout_reject'
  );
  const primaryCtaLabel =
    isTerminalBlocked  ? 'Dismiss'         :
    tone === 'failed'  ? 'Review failure'  :
    tone === 'done'    ? 'Open Bank Feed'  : 'View progress';
  const showPrimaryCta = true;

  const handlePrimaryCta = () => {
    if (isTerminalBlocked) {
      // No modal, no retry — just quietly remove from the tracker.
      try { removeJob && removeJob(headline.jobId); } catch (_) { /* noop */ }
      return;
    }
    if (tone === 'done') {
      navigate('/bank-feeds?tab=uploads');
      return;
    }
    // For 'failed' (transient) or 'active', open the modal on the headline job.
    setModalJobId(headline.jobId);
  };

  const handleCancel = async (job) => {
    if (!job?.jobId) return;
    if (cancellingIds.has(job.jobId)) return; // already in flight
    // iter1238 Phase 8.13b · Immediate visual feedback (was previously
    // 500-800 ms of silence between click and card removal).
    setCancellingIds((prev) => new Set(prev).add(job.jobId));
    try {
      await axios.post(`${API}/bank-feeds/upload-jobs/${job.jobId}/cancel`);
      toast.success('Upload cancelled');
    } catch (err) {
      // Even on server error we still want the local card to disappear
      // so the user isn't stuck watching a dead progress bar.
      const msg = err?.response?.data?.detail || 'Could not cancel on server; removing from tracker';
      toast.warning(msg);
    } finally {
      try { removeJob && removeJob(job.jobId); } catch (_) { /* noop */ }
      setCancellingIds((prev) => {
        const next = new Set(prev);
        next.delete(job.jobId);
        return next;
      });
    }
  };

  return (
    <div
      data-testid="global-upload-progress-banner"
      className={`w-full border-b ${toneClasses}`}
    >
      <div className="max-w-7xl mx-auto px-3 py-1 flex items-center gap-2">
        <FileText className="w-3.5 h-3.5 shrink-0 opacity-70" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className="text-xs font-medium truncate max-w-[28ch] sm:max-w-[42ch]"
              title={headline.filename}
              data-testid="global-upload-progress-filename"
            >
              {headline.filename || 'Statement'}
            </span>
            {icon}
            <span className="text-xs" data-testid="global-upload-progress-status">
              {statusPhrase(headline)}
              {tone === 'active' && headline.progress ? ` · ${pct}%` : ''}
            </span>
            {otherCount > 0 && (
              <span
                className="text-[10px] px-1 py-0 rounded bg-white/60 border border-current/20"
                data-testid="global-upload-progress-more-count"
              >
                +{otherCount} more
              </span>
            )}
          </div>
          {tone === 'active' && (
            <div className="mt-0.5 h-0.5 w-full bg-white/60 rounded overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="global-upload-progress-bar"
              />
            </div>
          )}
        </div>
        {headline.status === 'blocked_continuity' && headline.continuityReport?.case === 'continuity_mismatch' && headline.continuityReport?.missing_rows?.missing_count > 0 && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('akf-missing-rows-open', { detail: { jobId: headline.jobId, filename: headline.filename } }))}
            className="text-[11px] font-medium px-2 py-1 rounded bg-white/80 hover:bg-white border border-current/20 shrink-0"
            data-testid="global-upload-progress-missing-rows-cta"
          >
            {headline.continuityReport.missing_rows.can_add ? `Add ${headline.continuityReport.missing_rows.missing_count} missing rows` : 'Review missing rows'}
          </button>
        )}
        <button
          type="button"
          onClick={handlePrimaryCta}
          className={`text-[11px] font-medium px-2 py-1 rounded bg-white/80 hover:bg-white border border-current/20 shrink-0 ${showPrimaryCta ? '' : 'hidden'}`}
          data-testid="global-upload-progress-cta"
        >
          {primaryCtaLabel}
        </button>
        {tone === 'active' && activeJobs.length >= 1 && (() => {
          // Feb 2026 (iter1239 → iter1248) · Hide "Cancel" the moment
          // parsing succeeds and we transition into posting /
          // classifying / applying / finalizing. Cascade cleanup DOES
          // wipe partial artefacts, but by the time the user sees
          // "Posting transaction N of M" we've already spent AI
          // budget on classification — cancelling now just wastes
          // money AND briefly surfaces half-posted rows to reports.
          //
          // Previous version accidentally read
          //   `headline.progress?.progress_percent`
          // but `headline.progress` is a NUMBER (see
          // BackgroundJobsContext.jsx:113 & :209 which set
          // `progress: sj.progress_percent`). Reading `.progress_percent`
          // off a number always yielded undefined, so the threshold
          // check was permanently false and Cancel stayed visible
          // through the entire posting phase (screenshot filed
          // 2026-02-18: "Posting transaction 3 of 270 · 40%" with
          // Cancel still present).
          //
          // The fix is two-part:
          //   1. Reuse the already-computed `pct` (line 123).
          //   2. Trust the backend status_message as the primary
          //      signal — the moment it says posting / classifying /
          //      applying / finalising / saving / matching we are
          //      past the parsing preview and Cancel is unsafe.
          const msg = (headline?.statusMessage || '').toLowerCase();
          const inPostAppSlPhase = (
            msg.includes('posting') ||
            msg.includes('classif') ||
            msg.includes('applying') ||
            msg.includes('finaliz') ||
            msg.includes('finalis') ||
            msg.includes('saving') ||
            msg.includes('matching') ||
            msg.includes('creating journal') ||
            msg.includes('creating document') ||
            msg.includes('done')
          );
          const inPostingPhase = inPostAppSlPhase || pct >= 25;
          if (inPostingPhase) return null;
          return (
          <button
            type="button"
            onClick={() => handleCancel(headline)}
            disabled={cancellingIds.has(headline.jobId)}
            className={`text-[11px] font-medium px-2 py-1 rounded border shrink-0 flex items-center gap-1 transition-colors ${
              cancellingIds.has(headline.jobId)
                ? 'text-rose-500 border-rose-100 bg-rose-50/40 cursor-wait'
                : 'text-rose-700 hover:bg-rose-50 border-rose-200'
            }`}
            data-testid="global-upload-progress-cancel"
            title={cancellingIds.has(headline.jobId) ? 'Cancelling…' : `Cancel "${headline.filename}"`}
          >
            {cancellingIds.has(headline.jobId) ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Cancelling…
              </>
            ) : (
              'Cancel'
            )}
          </button>
          );
        })()}
        {(tone === 'done' || tone === 'failed') && (
          <button
            type="button"
            onClick={() => {
              visibleJobs
                .filter((j) => j.status === 'completed' || j.status === 'failed' || j.status === 'blocked_continuity')
                .forEach((j) => { try { removeJob && removeJob(j.jobId); } catch (_) { /* noop */ } });
            }}
            className="text-current/60 hover:text-current shrink-0"
            data-testid="global-upload-progress-dismiss"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <StreamingUploadModal
        open={!!modalJob}
        onOpenChange={(v) => { if (!v) setModalJobId(null); }}
        job={modalJob}
        onRetryDone={() => {
          // Backend flipped status back to processing; the
          // BackgroundJobsContext poll will pick it up on the next tick.
        }}
      />
    </div>
  );
};

export default GlobalUploadProgressBadge;
